import React from 'react';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import { useTranslation } from 'react-i18next';
import { DynamicMediaItem, Paragraph, CoreLink } from '@uncinc/react-drupal-core';
import type { ImmutableEntity, LinkField } from '@uncinc/react-drupal-core/types';
import Immutable from 'immutable';
import { WysiwygField, Share } from '../../../../components';
import ExpertsSlider from '../../../../components/ExpertsSlider';
import type { TaxonomyDossier as Type } from '../../../../types/taxonomy';
import './style.scss';

interface TaxonomyDossierProps {
  entity: ImmutableEntity<Type>;
}

const TaxonomyDossier: React.FC<TaxonomyDossierProps> = ({ entity }) => {
  const { t } = useTranslation();
  const taxonomy = entity.toJS();
  const title = taxonomy.name[0].value;
  const subtitle = taxonomy.field_sub_title[0]?.value;
  const description = taxonomy.description[0].processed;
  const image = taxonomy.field_media_image[0];
  const paragraphs = entity.getIn(['field_paragraphs'], Immutable.List());
  const dossierId = entity.getIn(['tid', 0, 'value']);
  const tableOfContents = entity.getIn(['field_table_of_contents']);
  const experts = entity.getIn(['field_experts'], Immutable.Map());
  const expertsTitle = entity.getIn(['field_experts_title', 0, 'value'], t('expert.node_title'));

  return (
    <div className="TaxonomyDossierFile">
      <div className="TaxonomyDossierFile__wrapper">
        <Container>
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="TaxonomyDossierFile__wrapper__content">
                <span className="TaxonomyDossierFile__wrapper__subtitle">
                  {subtitle}
                </span>
                <h1>{title}</h1>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <DynamicMediaItem
                // @ts-expect-error
                media={image}
                imageAspectRatio="2_1"
              />
            </div>
          </div>
        </Container>
      </div>

      <Container>
        <div className="row">
          <div className="col-12 col-lg-9 TaxonomyDossierFile__descriptionContainer">
            <WysiwygField
              property="schema:text"
              html={description}
              className="TaxonomyDossierFile__wysiwyg"
            />
          </div>

          <div className="col-12 col-lg-3 TaxonomyDossierFile__tableOfContent">
            <div className="TaxonomyDossierFile__tableOfContent__wrapper">
              <span className="TaxonomyDossierFile__tableOfContent__subtitle">
                {t('dossier.dossier_content')}
              </span>
              {tableOfContents.map((tableOfContentsItem: ImmutableEntity<LinkField>) => {
                const tableOfContentsItemId = tableOfContentsItem.getIn(['alias']);
                const tableOfContentsItemTitle = tableOfContentsItem.getIn(['title']);

                return (
                  <CoreLink className="TaxonomyDossierFile__tableOfContent__link" href={tableOfContentsItemId ?? '#'}>
                    <i className="icon icon-arrow-right" />
                    <span>{tableOfContentsItemTitle}</span>
                  </CoreLink>
                );
              })}
            </div>
          </div>
        </div>
      </Container>


      <div className="TaxonomyDossierFile__paragraphs">
        {paragraphs.map((item) => {
          const paragraphId = item.getIn(['entity', 'id', 0, 'value']);
          const paragraph = item.getIn(['entity']);

          return (
            <div id={paragraphId}>
              <Paragraph
                id={paragraphId}
                entity={paragraph}
                dossierId={dossierId}
              />
            </div>
          );
        })}
      </div>

      <div className="NodeContentWrapper">
        <ExpertsSlider
          expertsTitle={expertsTitle}
          experts={experts}
        />
      </div>

      <Container>
        <div className="row">
          <div className="col-12 col-lg-8 offset-lg-2">
            <Share />
          </div>
        </div>
      </Container>
    </div>
  );
};

export default TaxonomyDossier;
